import { type PropsWithChildren, type ReactElement } from "react"

import type { DashboardLayoutProps } from "./Interfaces"
import { ConfigSidebarContainer } from "./configurationsSidebar"
import { ModelsSidebarContainer } from "./sidebar"

import styles from "./ModelsLayout.module.scss"

// this object is a mapper for the different sidebar variant
// extend it if you need to add more variants
const SIDEBAR_MAPPER = {
  config: () => <ConfigSidebarContainer />,
  models: () => <ModelsSidebarContainer />,
}

/**
 * Renders the main layout for the models dashboard, including an optional header and sidebar.
 *
 * @param {DashboardLayoutProps} props - The component props.
 * @param {SidebarVariant} [props.sidebarVariant] - Determines which sidebar to display, if any.
 * @param {ReactElement} [props.header] - The header element to be rendered above the main content.
 * @param {ReactNode} props.children - The main content to be rendered.
 * @returns {ReactElement} The rendered layout component.
 */

export function ModelsLayout(props: Readonly<PropsWithChildren<DashboardLayoutProps>>): ReactElement {
  const { sidebarVariant, header, isSidebarOpen = true, children } = props
  return (
    <section
      className={`${styles.layoutGrid} ${sidebarVariant && isSidebarOpen ? styles[`${sidebarVariant}_sidebar`] : styles[`${sidebarVariant}_withoutSidebar`]}`}
    >
      {header && <header className={styles.header}>{header}</header>}
      {sidebarVariant && isSidebarOpen && (
        <section className={`${styles.sidebar} scrollbar`}> {SIDEBAR_MAPPER[sidebarVariant]()}</section>
      )}
      <section className={`${styles.mainSection} scrollbar`}>{children}</section>
    </section>
  )
}
